import { useEffect, useState } from "react";
import { IoIosArrowForward } from "react-icons/io";
import Select, { components } from "react-select";
import arrowup from '../Images/arrowup.svg'
import arrowdown from '../Images/arrowdown.svg'
import { GoCreditCard, GoPeople } from "react-icons/go";
import {
    Line,
    LineChart,
    XAxis,
    YAxis,
    ResponsiveContainer,
    CartesianGrid,
    Tooltip,
} from "recharts";
import { LuUserRoundPlus } from "react-icons/lu";
import { RiExpandUpDownFill } from "react-icons/ri";

const Control = ({ children, ...props }) => {
    return (
        <components.Control {...props}>
            <i className="ml-3 text-gray-500 ri-calendar-fill"></i>
            {children}
        </components.Control>
    );
};

const Users = ({ setDashboardView }) => {
    const [users, setUsers] = useState([]);
    const [userPaging, setUserPaging] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sortOrder, setSortOrder] = useState("asc");
    const [userData, setUserData] = useState({});
    const [pageNum, setPageNum] = useState(1);

    const getXAxisKey = (trend) => {
        if (!trend || trend.length === 0) return null;

        if ("yearly_created" in trend[0]) return "yearly_created";
        if ("day_created" in trend[0]) return "day_created";
        if ("month_created" in trend[0]) return "month_created";

        return null;
    };

    const formatHoverLabel = (value, key) => {
        const date = new Date(value);

        switch (key) {
            case "yearly_created":
                return date.getFullYear();
            case "month_created":
                return date.toLocaleDateString("en-US", {
                    month: "short",
                    year: "numeric",
                });
            case "day_created":
                return date.toLocaleDateString("en-US", {
                    day: "numeric",
                    month: "short",
                });
            default:
                return "";
        }
    };

    const TradingLineChart = () => {
        const trend = userData?.trend ?? [];

        if (trend.length === 0) {
            return (
                <div className="w-full h-56 flex items-center justify-center bg-[#FCFBFF] rounded-xl">
                    <p className="text-sm text-gray-400">No data available</p>
                </div>
            );
        }

        const xKey = getXAxisKey(trend);
        const values = trend.map((d) => d.size ?? 0);
        const min = Math.min(...values);
        const max = Math.max(...values);
        const mid = (min + max) / 2;

        return (
            <div className="w-full h-56 bg-[#FCFBFF] rounded-xl p-3">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trend}>
                        <XAxis
                            dataKey={xKey}
                            axisLine={false}
                            interval={0}
                            padding={{ left: 20, right: 20 }}
                            tickLine={false}
                            tick={{ fontSize: 12, fill: "#6B7280" }}
                            tickFormatter={(value) => formatHoverLabel(value, xKey)}
                        />
                        <YAxis hide domain={[min, max]} ticks={[min, mid, max]} />
                        <CartesianGrid
                            horizontal
                            vertical={false}
                            stroke="#EDE9FE"
                            strokeDasharray="3 3"
                        />
                        <Tooltip
                            labelFormatter={(label) => formatHoverLabel(label, xKey)}
                            formatter={(value) => [`${value}`, "Users"]}
                        />
                        <Line
                            type="monotone"
                            dataKey="size"
                            stroke="#7C3AED"
                            strokeWidth={2}
                            dot={false}
                            activeDot={{ r: 4 }}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        );
    };

    const yearOpt = [
        { value: "", label: "Lifetime" },
        { value: "2025", label: "2025" },
        { value: "2026", label: "2026" },
        { value: "2027", label: "2027" },
        { value: "2028", label: "2028" },
        { value: "2029", label: "2029" },
        { value: "2030", label: "2030" },
    ];

    const monthOptions = [
        { value: "", label: "All Month" },
        { value: "1", label: "Jan" },
        { value: "2", label: "Feb" },
        { value: "3", label: "Mar" },
        { value: "4", label: "Apr" },
        { value: "5", label: "May" },
        { value: "6", label: "Jun" },
        { value: "7", label: "Jul" },
        { value: "8", label: "Aug" },
        { value: "9", label: "Sep" },
        { value: "10", label: "Oct" },
        { value: "11", label: "Nov" },
        { value: "12", label: "Dec" },
    ];

    const filterOpt = [
        { value: "", label: "Filters" },
        { value: "wedding", label: "Wedding Status" },
        { value: "paid", label: "Paid Status" },
    ];

    const [selectedYear, setSelectedYear] = useState(yearOpt[0]);
    const [selectedMonth, setSelectedMonth] = useState(monthOptions[0]);
    const [selectedFilter, setSelectedFilter] = useState(filterOpt[0]);

    const handleYearChange = (option) => {
        setSelectedYear(option);
        setSelectedMonth(monthOptions[0]);
    };

    const handleMonthChange = (option) => {
        setSelectedMonth(option);
    };

    const handleFilterChange = (option) => {
        setSelectedFilter(option);
    };

    useEffect(() => {
        const fetchUsersAndData = async () => {
            try {
                const params = new URLSearchParams();

                if (selectedYear?.value) {
                    params.append("year", selectedYear.value);
                }
                if (selectedMonth?.value) {
                    params.append("month", selectedMonth.value);
                }

                const token = localStorage.getItem("PhilomenaAdminToken");

                // Fetch analytics
                const res = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/admin/user_analytics?${params.toString()}`,
                    {
                        method: "GET",
                        headers: { Authorization: token ? `Token ${token}` : "" },
                    }
                );
                const data = await res.json();
                setUserData(data || {});

                // Fetch paginated user list (source of truth for total count)
                const res2 = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/admin/get_user_list?page=${pageNum}`,
                    {
                        method: "GET",
                        headers: { Authorization: `Token ${token}` },
                    }
                );
                const data2 = await res2.json();

                setUsers(data2.results || []);
                setUserPaging(data2);

                // === DEBUG LOGS ===
                console.log("=== USERS PAGE ===");
                console.log("user_analytics user_count:", data?.user_count);
                console.log("get_user_list count (RELIABLE):", data2?.count);
                console.log("Full user_analytics:", data);
                console.log("Full user list response:", data2);

            } catch (err) {
                console.error("Error fetching users:", err);
                setError("Failed to load users.");
            } finally {
                setLoading(false);
            }
        };

        fetchUsersAndData();
    }, [selectedYear, selectedMonth, pageNum]);

    const customStyles = {
        indicatorSeparator: () => ({ display: 'none' }),
        control: (base) => ({
            ...base,
            borderColor: '#F3F0F7',
            paddingTop: '0px',
            paddingBottom: '0px',
            paddingLeft: '7px',
            boxShadow: '0 1px 2px 0 #1D1C1F0D',
            borderRadius: '10px',
            borderWidth: '1px',
            width: 'max-content',
        }),
        menu: (base) => ({ ...base }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isFocused ? '#F3F0F7' : 'white',
            color: 'black',
            padding: 10,
            cursor: 'pointer',
        }),
    };

    const filterUsers = users.filter((user) => {
        const status = user?.status === 'Paid' ? 'paid' : "";
        const wedding = user?.wedding_status === 'Created' ? 'wedding' : "";
        const term = selectedFilter.value.toLowerCase();

        return status.includes(term) || wedding.includes(term);
    });

    const handleSortByName = () => {
        const sorted = [...users].sort((a, b) => {
            const nameA = a.name?.toLowerCase() || "";
            const nameB = b.name?.toLowerCase() || "";

            if (nameA < nameB) return sortOrder === "asc" ? -1 : 1;
            if (nameA > nameB) return sortOrder === "asc" ? 1 : -1;
            return 0;
        });

        setUsers(sorted);
        setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    };

    const pct = Math.ceil(userData?.pct_change ?? 0);

    const getBadgeStyles = () => {
        if (pct > 0) return "bg-[#D1FAE4] text-[#166E3F]";
        if (pct < 0) return "bg-red-100 text-red-600";
        return "bg-gray-100 text-gray-600";
    };

    const getArrow = () => {
        if (pct > 0) return arrowup;
        if (pct < 0) return arrowdown;
        return arrowup;
    };

    if (loading) {
        return (
            <div className="text-center py-4 flex flex-col items-center justify-center h-[90vh]">
                <div className="w-8 h-8 border-b-2 border-purple-500 rounded-full animate-spin"></div>
                <span className="text-gray-600">Loading...</span>
            </div>
        );
    }

    if (error) {
        return <p className="py-5 text-red-600 sm:py-10">{error}</p>;
    }

    const totalUsers = userPaging?.count ?? userData?.user_count ?? 0;

    return (
        <div className="py-5 space-y-8 overflow-x-hidden shadow-sm sm:py-10 rounded-2xl">
            <div className="space-y-7">
                <p className="text-2xl font-medium">Users</p>

                <div className="flex flex-col-reverse items-start justify-between w-full gap-4 sp:flex-row sm:w-auto">
                    <div className="space-y-1">
                        <p className="text-[#0F132466] font-medium">User Acquisition Trend</p>
                        <p className="text-[28px] font-medium">{totalUsers}</p>
                        <div className="flex items-center gap-1">
                            <div className={`flex items-center gap-2 px-2 py-1 rounded-lg text-sm font-medium ${getBadgeStyles()}`}>
                                <img
                                    src={getArrow()}
                                    alt=""
                                    className={pct === 0 ? "rotate-90 opacity-60" : ""}
                                />
                                <p>{pct}%</p>
                            </div>

                            <p className="text-sm text-[#0F132466] font-medium">
                                {userData?.delta < 0
                                    ? `- ${Math.abs(userData.delta)}`
                                    : `+ ${userData.delta}`} users
                                from last 30 days
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <Select
                            options={yearOpt}
                            value={selectedYear}
                            onChange={handleYearChange}
                            styles={customStyles}
                            className="w-full"
                            menuPlacement="auto"
                            isSearchable={false}
                        />
                        {selectedYear.value !== '' && (
                            <Select
                                options={monthOptions}
                                value={selectedMonth}
                                onChange={handleMonthChange}
                                placeholder="Select Month"
                                styles={customStyles}
                                className="w-full"
                                menuPlacement="auto"
                                isSearchable={false}
                            />
                        )}
                    </div>
                </div>
            </div>

            <TradingLineChart />

            <div className="flex items-center justify-between w-full gap-4 sm:w-auto">
                <p className="text-[#0F132466] font-medium">Summary</p>
            </div>

            <div className="grid items-start gap-3 mc:grid-cols-3 sm:grid-cols-2 sm:justify-center sm:gap-5 mt-7">
                <div className="text-black/50 w-full sm:p-6 p-3 h-full rounded-md flex flex-col justify-between border-[1.5px] border-[#EBECEC] bg-white">
                    <div className="flex items-center justify-between">
                        <div className="space-y-2">
                            <p className="text-[16px] font-medium">Paid Users</p>
                            <p className="sm:text-4xl text-[22px] text-black font-[450]">
                                {userData.paid_users ?? 0}
                            </p>
                        </div>
                        <div className="bg-[#62259A0A] rounded-full text-[#62259A] text-xl size-12 flex items-center justify-center">
                            <GoPeople />
                        </div>
                    </div>
                </div>

                <div className="text-black/50 w-full sm:p-6 p-3 h-full rounded-md flex flex-col justify-between border-[1.5px] border-[#EBECEC] bg-white">
                    <div className="flex items-center justify-between">
                        <div className="space-y-2">
                            <p className="text-[16px] font-medium">Unpaid Users</p>
                            <p className="sm:text-4xl text-[22px] text-black font-[450]">
                                {userData.unpaid_users ?? 0}
                            </p>
                        </div>
                        <div className="bg-[#62259A0A] rounded-full text-[#62259A] text-xl size-12 flex items-center justify-center">
                            <LuUserRoundPlus />
                        </div>
                    </div>
                </div>

                <div className="text-black/50 w-full sm:p-6 p-3 h-full rounded-md flex flex-col justify-between border-[1.5px] border-[#EBECEC] bg-white">
                    <div className="flex items-center justify-between">
                        <div className="space-y-2">
                            <p className="text-[16px] font-medium">New Users Today</p>
                            <p className="sm:text-4xl text-[22px] text-black font-[450]">
                                {userData.new_users_today ?? 0}
                            </p>
                        </div>
                        <div className="bg-[#62259A0A] rounded-full text-[#62259A] text-xl size-12 flex items-center justify-center">
                            <GoCreditCard />
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex items-start justify-start gap-4 w-fit pt-7">
                <Select
                    components={{ Control }}
                    options={filterOpt}
                    value={selectedFilter}
                    onChange={handleFilterChange}
                    placeholder="Select Filter"
                    styles={customStyles}
                    className="w-full"
                    menuPlacement="auto"
                    isSearchable={false}
                />
            </div>

            <div>
                <div className="mb-3 font-medium">
                    {userPaging.page_size} Users of {totalUsers} Total Users
                </div>
                <div className="overflow-x-auto rounded-[10px] border" style={{ WebkitOverflowScrolling: "touch" }}>
                    <table className="w-full min-w-[1000px] text-nowrap border-collapse">
                        <thead>
                            <tr className="bg-[#F7F7F8] text-left text-[#0F132499] text-sm">
                                <th onClick={handleSortByName} className="flex items-center gap-1 px-3 py-3 cursor-pointer">
                                    <span>User Detail</span>
                                    <RiExpandUpDownFill className="size-4" />
                                </th>
                                <th className="px-3 py-3">Status</th>
                                <th className="px-3 py-3">Wedding Status</th>
                                <th className="px-3 py-3">Date Joined</th>
                                <th className="px-3 py-3 text-end"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.length > 0 ? (
                                filterUsers.map((user) => (
                                    <tr
                                        key={user.id}
                                        onClick={() => {
                                            window.scrollTo(0, 0);
                                            setDashboardView('userDetails');
                                            localStorage.setItem('dashboardView', 'userDetails');
                                            localStorage.setItem('selectedUserId', user?.id);
                                        }}
                                        className="border-b hover:bg-[#F7F7F8] cursor-pointer text-sm text-gray-800"
                                    >
                                        <td className="flex items-center gap-2 px-3 py-3">
                                            <div className="flex items-center justify-center size-10 rounded-full bg-[#62259A] text-white font-medium">
                                                {user?.initials}
                                            </div>
                                            <div className="flex flex-col">
                                                <p className="font-medium">{user?.name}</p>
                                                <p className="text-[#0F132499]">{user?.email}</p>
                                            </div>
                                        </td>
                                        <td className="px-3 py-3">
                                            {user?.status === "Paid" ? (
                                                <span className="inline-flex rounded-lg px-2 py-0.5 text-sm leading-4 text-green-500 bg-green-100 font-medium">
                                                    Paid
                                                </span>
                                            ) : (
                                                <span className="inline-flex rounded-lg px-2 py-0.5 text-sm leading-4 bg-[#FDEAD8] text-[#AE590A] font-medium">
                                                    Pending
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3 py-3">
                                            {user?.wedding_status === 'Created' ? (
                                                <span className="inline-flex rounded-lg px-2 py-0.5 text-sm leading-4 text-[#5314A3] bg-[#ECDFFB] font-medium">
                                                    Created
                                                </span>
                                            ) : (
                                                <span className="inline-flex rounded-lg px-2 py-0.5 text-sm leading-4 bg-[#E9EAEC] text-[#0F132499] font-medium">
                                                    Not Created
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3 py-3 font-medium">
                                            {new Date(user?.date_joined).toLocaleString("en-US", {
                                                day: "numeric",
                                                month: "short",
                                                year: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                            })}
                                        </td>
                                        <td className="px-3 py-3 text-end">
                                            <IoIosArrowForward />
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="px-3 py-3 text-center text-gray-600">
                                        No users found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="mt-3 font-medium">Page {userPaging.page} of {userPaging.num_pages}</div>
            </div>

            <div className="flex items-center justify-center gap-4">
                <button
                    onClick={() => setPageNum(prev => prev > 1 ? prev - 1 : 1)}
                    className={`px-4 py-2 rounded-xl border border-gray-200 hover:bg-gray-100 ${userPaging?.page === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                    Prev
                </button>
                <button
                    onClick={() => setPageNum(prev => prev < userPaging?.num_pages ? prev + 1 : prev)}
                    className={`px-4 py-2 rounded-xl border border-gray-200 hover:bg-gray-100 ${userPaging?.page === userPaging?.num_pages ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                    Next
                </button>
            </div>
        </div>
    );
};

export default Users;